import { AxiosResponse } from 'axios';
import { bipApiClient, getBipAuthHeaders } from './index';
import {
  TweetContentDto,
  ContentType,
  ApiResponse,
  UpdateTweetRequest,
} from './types';

export const tweetContentApi = {
  /**
   * Generate a new tweet content for a token
   * @param tokenAddress Token address
   * @param contentType Content type (FULL_REPORT, SMART_MONEY, LORE)
   * @returns Generated tweet content
   */
  async generateTweet(
    tokenAddress: string,
    contentType: ContentType
  ): Promise<TweetContentDto> {
    try {
      const response: AxiosResponse<ApiResponse<TweetContentDto>> =
        await bipApiClient.post(
          `api/v1/tokens/${tokenAddress}/tweet-contents/generate`,
          { contentType },
          {
            headers: getBipAuthHeaders(),
            timeout: 300000, // 5 minutes - this API takes a long time
          }
        );

      return response.data.data;
    } catch (error) {
      console.error(
        '❌ POST /v1/tokens/:address/tweet-contents/generate error:',
        error
      );
      throw error;
    }
  },

  /**
   * Get all tweet contents for a token
   * @param tokenAddress Token address
   * @returns List of tweet contents
   */
  async getTweetContents(tokenAddress: string): Promise<TweetContentDto[]> {
    try {
      const response: AxiosResponse<ApiResponse<TweetContentDto[]>> =
        await bipApiClient.get(`api/v1/tokens/${tokenAddress}/tweet-contents`, {
          headers: getBipAuthHeaders(),
        });

      return response.data.data;
    } catch (error) {
      console.error(
        '❌ GET /v1/tokens/:address/tweet-contents error:',
        error
      );
      throw error;
    }
  },

  /**
   * Update a tweet content (only for PENDING status)
   * @param id Tweet content ID
   * @param contents Updated contents array
   * @returns Updated tweet content
   */
  async updateTweetContent(
    id: string,
    contents: string[]
  ): Promise<TweetContentDto> {
    try {
      const request: UpdateTweetRequest = { contents };
      const response: AxiosResponse<ApiResponse<TweetContentDto>> =
        await bipApiClient.put(`api/v1/tweet-contents/${id}`, request, {
          headers: getBipAuthHeaders(),
        });

      return response.data.data;
    } catch (error) {
      console.error('❌ PUT /v1/tweet-contents/:id error:', error);
      throw error;
    }
  },

  /**
   * Publish a tweet content
   * @param id Tweet content ID
   * @returns Published tweet content
   */
  async publishTweet(id: string, isPersonalTweet: boolean = false): Promise<TweetContentDto> {
    try {
      const response: AxiosResponse<ApiResponse<TweetContentDto>> =
        await bipApiClient.post(
          `api/v1/tweet-contents/${id}/publish`,
          { isPersonalTweet },
          {
            headers: getBipAuthHeaders(),
          }
        );

      return response.data.data;
    } catch (error) {
      console.error('❌ POST /v1/tweet-contents/:id/publish error:', error);
      throw error;
    }
  },
};
